import { Badge, Button, Group, SegmentedControl, Stack, Table, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EmptyNote, RecordScreen } from '../../components/RecordScreen';
import { PaperRowControl } from '../../components/ThePaperItself';
import { describeWeekdays } from '../../components/WeekdaysField';
import { useIslandToday, useLocationId } from '../../hooks/useSeedLocation';
import { enumColor, enumLabel } from '../../lib/enums';
import { formatDate, formatMoney, toIsoDate } from '../../lib/format';
import { BillSubject, billSubject } from './billSubject';
import { ContactsPanel } from './ContactsPanel';
import { contactsPath, ReachLink, type SupplierContact, waysToReach, whoToCall } from './contacts';
import { type Bill, RecordPaymentForm } from './PayablesSection';
import { RecordBillButton } from './RecordBillForm';
import { ApplyCreditForm, RecordCreditButton } from './RecordCreditForm';
import { SupplierCatalogSection } from './SupplierCatalogSection';
import { OrderFromThemButton, SupplierOrdersSection } from './SupplierOrdersSection';
import { ScorecardCard, useScorecards } from './SupplierScorecardSection';

const SUPPLIER_INVOICES_PATH = '/admin/v1/suppliers/supplier-invoices';
const PAYABLES_PATH = '/admin/v1/suppliers/payables';

interface Supplier {
  id: string;
  name: string;
  kind?: string;
  island?: boolean;
  invoiceCurrency: string;
  paymentTermsDays?: number;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  contactName?: string | null;
  rtn?: string | null;
  minimumOrderMinor?: number | null;
  /** 0 = Sunday … 6 = Saturday. Empty means nobody has written down when they come. */
  deliveryDays?: number[] | null;
  defaultLeadTimeDays?: number | null;
  notes?: string | null;
  active?: boolean;
}

interface Payable {
  invoice: {
    id: string;
    supplierId: string;
    invoiceNumber?: string | null;
    /**
     * What the bill was FOR. Set on a bill with nothing to count behind it — the propane, the ice —
     * where it is the only thing that says which bill this is. Null on one a delivery opened, whose
     * receipt lines say it instead.
     */
    description?: string | null;
    /** Set when a delivery opened this bill — which is the answer to "what for" on those rows. */
    receiptId?: string | null;
    invoiceDate: string;
    dueDate?: string | null;
    currency: string;
    totalMinor: number;
    paidMinor: number;
    status: string;
    /** Set on a credit note, naming the bill it comes off. Null while it stands on its own. */
    referencesInvoiceId?: string | null;
    /** What the delivery behind it came to. Null when there is no delivery to disagree with. */
    receivedTotalMinor?: number | null;
    /** Why the paper and the delivery disagree, said by whoever took it in. */
    discrepancyReason?: string | null;
    /** The photographed bill, when somebody took one. Null means nobody did — not that it is lost. */
    sourceDocumentId?: string | null;
  };
  agingDays: number;
  /** What the supplier has credited back against this bill. */
  creditedMinor: number;
  /** Billed, less paid, less credited — worked out once, on the server. */
  outstandingMinor: number;
  /** Billed less what arrived. Positive: they billed for more than turned up. */
  discrepancyMinor: number;
  /** How much of that difference nobody has answered yet. */
  discrepancyOpenMinor: number;
}

/**
 * A credit note is a bill with the sign the other way round, and that sign is the whole test.
 *
 * Deliberately not a list of its own on this page. The owner's question is "what has this vendor
 * sent me", and a credit is one of the things they send; two tables would mean doing the
 * subtraction by eye.
 */
const isCredit = (invoice: { totalMinor: number }) => invoice.totalMinor < 0;

interface FreightCharge {
  id: string;
  carrierId: string;
  reference?: string | null;
  amountMinor: number;
  currency: string;
  chargedAt: string;
  status: string;
}

/** Periods a manager actually asks about, rather than a pair of date boxes to fill in. */
const PERIODS = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last year' },
  { value: 'all', label: 'Everything' },
];

/**
 * "Last 90 days" counted back from the bar's day, not from Greenwich.
 *
 * `new Date().toISOString().slice(0, 10)` is the banned pattern by name; it is Greenwich's calendar,
 * and the six evening hours it gets wrong on Roatán are the hours the boat paperwork is typed in.
 * A window edge is cheaper than a stored date, which is why this one outlived two sweeps of the
 * same defect — and why the check that now enforces the rule is worth more than either sweep.
 */
function since(period: string, today: string): string {
  if (period === 'all') return '0000-01-01';
  const d = new Date(`${today}T12:00:00`);
  d.setDate(d.getDate() - Number(period));
  return toIsoDate(d) ?? today;
}

/**
 * One vendor, and everything you have done with them.
 *
 * The question a manager actually has about a supplier is not "what are their delivery terms" —
 * it is "how much am I spending here, what do I still owe, and what did I buy". A row in a table
 * cannot answer that, and a per-carrier spend widget answers a narrower version of it for only
 * some vendors. A carrier is just a vendor whose bills happen to be crossings, so it gets the same
 * screen rather than a special one.
 */
export function SupplierDetailPage() {
  const { supplierId = '' } = useParams();
  const locationId = useLocationId();
  const islandToday = useIslandToday();
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState('90');
  const [paying, setPaying] = useState<Bill | null>(null);
  /** A credit standing on its own, about to be put against a bill. */
  const [spending, setSpending] = useState<{
    id: string;
    amountMinor: number;
    currency: string;
    subject: string;
  } | null>(null);
  const from = since(period, islandToday);

  const suppliersQuery = useQuery({
    queryKey: listKey('/admin/v1/suppliers/suppliers'),
    queryFn: () => rawApi<Supplier[]>('GET', '/admin/v1/suppliers/suppliers'),
  });
  const supplier = (suppliersQuery.data ?? []).find((s) => s.id === supplierId);

  // Every bill they have sent, not only the unpaid ones. Built on the payables list, this section
  // emptied itself the moment a bill was settled and "Spent" fell back to nothing — so a vendor
  // who is paid on time looked like a vendor nobody buys from.
  const billsQuery = useQuery({
    queryKey: listKey(SUPPLIER_INVOICES_PATH, { supplierId }),
    queryFn: () => rawApi<Payable[]>('GET', `${SUPPLIER_INVOICES_PATH}?supplierId=${supplierId}`),
    enabled: Boolean(supplierId),
  });

  const freightQuery = useQuery({
    queryKey: listKey('/admin/v1/suppliers/freight-charges', { carrierId: supplierId }),
    queryFn: () =>
      rawApi<FreightCharge[]>('GET', `/admin/v1/suppliers/freight-charges?carrierId=${supplierId}`),
    enabled: Boolean(supplierId),
  });

  // Same key the contacts section uses, so the header and the section are one request, not two.
  const contactsQuery = useQuery({
    queryKey: listKey(contactsPath(supplierId)),
    queryFn: () => rawApi<SupplierContact[]>('GET', contactsPath(supplierId)),
    enabled: Boolean(supplierId),
  });
  const callFirst = whoToCall(contactsQuery.data ?? []);
  // The vendor's own columns are the fallback for a record written straight through the API without
  // anyone on file. It is deliberately NOT used while the contacts are still in flight: the two
  // agree on the name but the contact also carries their job and their branch, so falling back
  // early would print a bare name and then visibly grow a second later.
  const fallback = contactsQuery.isPending ? undefined : supplier;
  const orderBy = waysToReach(callFirst ?? { whatsapp: fallback?.whatsapp, phone: fallback?.phone })[0];

  const bills = (billsQuery.data ?? [])
    .filter((p) => p.invoice.invoiceDate >= from)
    .sort((a, b) => (a.invoice.invoiceDate < b.invoice.invoiceDate ? 1 : -1));
  const crossings = (freightQuery.data ?? [])
    .filter((c) => c.status === 'posted' && c.chargedAt >= from)
    .sort((a, b) => (a.chargedAt < b.chargedAt ? 1 : -1));

  /**
   * Totals are kept per currency and never added together: this bar is billed in both lempiras and
   * dollars, and one combined figure would be a number that is not money.
   */
  const spendByCurrency = new Map<string, number>();
  const owedByCurrency = new Map<string, number>();
  /** Credits they have issued that are not against any bill yet — money they owe you. */
  const creditByCurrency = new Map<string, number>();
  for (const { invoice } of bills) {
    // A credit note's total is negative, so this sum nets without being told credits exist: money
    // that came back is not money spent, from the day the credit is dated.
    spendByCurrency.set(invoice.currency, (spendByCurrency.get(invoice.currency) ?? 0) + invoice.totalMinor);
  }
  /*
   * WHAT IS OWED AND WHAT THEY OWE YOU ARE READ FROM EVERY BILL ON FILE, not from the ones dated
   * inside the period above. Only SPEND is a question about a period.
   *
   * "What did I spend here in the last ninety days" is a real question and the selector answers it.
   * "How much do I still owe them" is not: an unpaid bill is owed whatever month it was written in,
   * and this bar's suppliers deliver by boat, so the bill that goes unpaid longest is exactly the
   * one that falls out of the window first. Measured on 2026-07-28 before this was changed: on the
   * default Last 90 days the header read the truth, and one click to Last 30 days made it read
   * L 2,000.00 against a vendor who was owed L 4,400.00 — a bill twenty-three days late had simply
   * left the screen, while sitting on Bills to pay the whole time. A figure headed "Still owed"
   * that silently means "still owed, of the bills I am currently showing you" is worse than no
   * figure, because nothing on the card says a window was applied to it.
   *
   * The same argument already stood beside the credit below and had only ever been applied there.
   */
  for (const { invoice, outstandingMinor } of billsQuery.data ?? []) {
    if (outstandingMinor > 0) {
      owedByCurrency.set(invoice.currency, (owedByCurrency.get(invoice.currency) ?? 0) + outstandingMinor);
    }
    if (!isCredit(invoice) || invoice.referencesInvoiceId) continue;
    creditByCurrency.set(
      invoice.currency,
      (creditByCurrency.get(invoice.currency) ?? 0) - invoice.totalMinor,
    );
  }
  for (const c of crossings) {
    spendByCurrency.set(c.currency, (spendByCurrency.get(c.currency) ?? 0) + c.amountMinor);
  }

  const isCarrier = supplier?.kind === 'freight' || supplier?.kind === 'both';

  return (
    <RecordScreen
      backTo="/suppliers"
      backLabel="All suppliers"
      isLoading={suppliersQuery.isLoading}
      notFound={!suppliersQuery.isLoading && !supplier}
      notFoundMessage="This supplier is no longer on file."
      title={supplier?.name ?? ''}
      subtitle={
        callFirst
          ? [callFirst.name, callFirst.role, callFirst.branchName].filter(Boolean).join(' · ')
          : (fallback?.contactName ?? undefined)
      }
      status={
        supplier?.kind
          ? {
              label: enumLabel('supplierKind', supplier.kind),
              color: enumColor('supplierKind', supplier.kind),
            }
          : undefined
      }
      actions={<SegmentedControl size="xs" data={PERIODS} value={period} onChange={setPeriod} />}
      facts={
        supplier
          ? [
              // Spend is the one fact here the selector beside it moves, so it says so. What is
              // owed either way does not move, and a reader who watches one figure change and the
              // other stay put should be able to see from the labels that both are behaving.
              ...[...spendByCurrency.entries()].map(([currency, total]) => ({
                label: `Spent in these dates (${currency})`,
                value: formatMoney(total, currency),
              })),
              ...(spendByCurrency.size === 0
                ? [{ label: 'Spent in these dates', value: null, empty: 'Nothing yet' }]
                : []),
              ...[...owedByCurrency.entries()].map(([currency, owed]) => ({
                label: `Still owed (${currency})`,
                value: formatMoney(owed, currency),
              })),
              // Money going the other way, and the owner has to be able to see it: a credit that is
              // not against any bill is worth nothing at all unless somebody remembers it is there
              // when the next bill arrives.
              ...[...creditByCurrency.entries()].map(([currency, credit]) => ({
                label: `They owe you (${currency})`,
                value: formatMoney(credit, currency),
              })),
              { label: 'Based', value: supplier.island ? 'On the island' : 'Mainland' },
              { label: 'Bills you in', value: enumLabel('currency', supplier.invoiceCurrency) },
              {
                label: 'Terms',
                value: supplier.paymentTermsDays ? `Within ${supplier.paymentTermsDays} days` : 'On delivery',
              },
              {
                // The fact that decides when an order has to go out. Empty is not a dash: it is
                // the thing to go and record, and the label says so.
                label: 'They come',
                value: describeWeekdays(supplier.deliveryDays),
                empty: 'Not recorded',
              },
              {
                label: 'Order by',
                value: orderBy ? <ReachLink way={orderBy} /> : null,
                empty: 'No number',
              },
              ...(supplier.rtn ? [{ label: 'RTN', value: supplier.rtn }] : []),
            ]
          : []
      }
      sections={[
        {
          // First, because it is what a manager opens a vendor for at eight in the morning: the
          // bills matter at month end, the number to call matters today.
          title: 'Who to call',
          // Deliberately short. The longer version explained the grouping — company-wide first,
          // then each of their places — on every vendor, including the majority who have one person
          // and no places and would read a description of machinery they will never see. Where
          // there IS grouping the headings say so themselves.
          description: 'The people you deal with here.',
          children: supplier ? <ContactsPanel supplierId={supplier.id} supplierName={supplier.name} /> : null,
        },
        {
          // Above the bills because it is the daily job. Ordering from this vendor used to mean
          // leaving this vendor: the supplier list, the Orders tab, and picking them out of a
          // dropdown, from a page that already knew who they were and what they charge.
          title: 'Orders',
          description:
            'What you have asked them for. Open one to see what was on it and record the delivery.',
          actions: supplier ? <OrderFromThemButton supplierId={supplier.id} locationId={locationId} /> : null,
          children: supplier ? (
            <SupplierOrdersSection
              supplierId={supplier.id}
              supplierName={supplier.name}
              locationId={locationId}
            />
          ) : null,
        },
        {
          title: 'Bills',
          description:
            period === 'all'
              ? 'Every invoice they have sent you.'
              : `Invoices dated since ${formatDate(from)}.`,
          // A bill arrives here two ways and only one of them had a door. A delivery opens one as a
          // side effect of the goods being received, from the order; the gas man's bill for filling
          // the propane tank has no goods behind it at all, and recording it meant inventing a stock
          // item called "Propane" and a quantity nobody counts. This is that door, on the page that
          // already knows whose bill it is — beside the "Record a payment" that settles them.
          // Two doors, because two different things arrive from a vendor. "Record a credit" is not
          // a negative amount on the bill form: a bill asks when it has to be paid, a credit asks
          // which bill it comes off, and neither question makes sense on the other form.
          actions: supplier ? (
            <Group gap="xs">
              <RecordCreditButton vendor={supplier} />
              <RecordBillButton vendor={supplier} />
            </Group>
          ) : null,
          children:
            bills.length === 0 ? (
              <EmptyNote>No invoices in this period.</EmptyNote>
            ) : (
              <Table.ScrollContainer minWidth={560}>
                <Table verticalSpacing="sm" highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      {/* Headed for what the cell actually holds. It said "Invoice" above
                          "Filling the propane tank", while the same column on Bills to pay is
                          headed "What for" — one fact under two names, a page apart. */}
                      <Table.Th>What for</Table.Th>
                      <Table.Th>Dated</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th ta="right">Total</Table.Th>
                      <Table.Th ta="right">Still owed</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {bills.map(
                      ({ invoice, agingDays, creditedMinor, outstandingMinor, discrepancyOpenMinor }) => {
                        const credit = isCredit(invoice);
                        return (
                          <Table.Tr key={invoice.id}>
                            {/* Shared with the Bills-to-pay list rather than written twice: this
                              table put the invoice number first and that one put the description
                              first, so the same bill read differently on the two screens. */}
                            <Table.Td>
                              <BillSubject bill={invoice} strong />
                              {/* The paper itself, on the page somebody opens with the vendor's
                                  statement in front of them. */}
                              <PaperRowControl
                                documentId={invoice.sourceDocumentId}
                                attachPath={`${SUPPLIER_INVOICES_PATH}/${invoice.id}/document`}
                                subject={billSubject(invoice)}
                                invalidate={[
                                  listKey(SUPPLIER_INVOICES_PATH, { supplierId: invoice.supplierId }),
                                  listKey(SUPPLIER_INVOICES_PATH),
                                  listKey(PAYABLES_PATH),
                                ]}
                              />
                              {/* Which way the money went, in words, on the row where the only other
                                clue is a minus sign in a column of numbers. */}
                              {credit && (
                                <Text size="xs" c="dimmed">
                                  {invoice.referencesInvoiceId
                                    ? 'Credit, against a bill'
                                    : 'Credit, not against a bill yet'}
                                </Text>
                              )}
                              {/* The bill and the delivery behind it do not agree, said on the row
                                rather than only on the list that collects them — this is the page
                                somebody opens with the vendor's statement in front of them, and
                                "why is this L 1,350 more than the goods" is the question it is
                                opened to answer. */}
                              {discrepancyOpenMinor !== 0 && (
                                <Text size="xs" c={discrepancyOpenMinor > 0 ? 'orange.8' : 'blue.8'}>
                                  {discrepancyOpenMinor > 0
                                    ? `${formatMoney(discrepancyOpenMinor, invoice.currency)} billed and not delivered`
                                    : `${formatMoney(-discrepancyOpenMinor, invoice.currency)} delivered and not billed`}
                                  {invoice.discrepancyReason
                                    ? ` · ${enumLabel('billDiscrepancyReason', invoice.discrepancyReason)}`
                                    : ''}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {formatDate(invoice.invoiceDate)}
                              {outstandingMinor > 0 && agingDays > 0 && (
                                <Text size="xs" c={agingDays > 30 ? 'red.7' : 'dimmed'}>
                                  {/* "1 days ago" is what a screen nobody proof-read says. */}
                                  {agingDays} {agingDays === 1 ? 'day' : 'days'} ago
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Badge
                                variant="light"
                                color={enumColor('payableStatus', invoice.status) ?? 'gray'}
                              >
                                {enumLabel('payableStatus', invoice.status)}
                              </Badge>
                            </Table.Td>
                            <Table.Td ta="right">
                              {formatMoney(invoice.totalMinor, invoice.currency)}
                              {creditedMinor > 0 && (
                                <Text size="xs" c="dimmed">
                                  {formatMoney(creditedMinor, invoice.currency)} credited back
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td ta="right" fw={outstandingMinor > 0 ? 700 : 400}>
                              {outstandingMinor > 0 ? formatMoney(outstandingMinor, invoice.currency) : '—'}
                            </Table.Td>
                            {/* On the bill, on the page that is showing it as owed. Paying it used
                              to mean going back to the supplier list, over to Bills to pay, and
                              finding this same invoice among every supplier's. */}
                            <Table.Td ta="right">
                              {outstandingMinor > 0 && (
                                <Button
                                  size="xs"
                                  variant="light"
                                  onClick={() =>
                                    setPaying({ ...invoice, agingDays, creditedMinor, outstandingMinor })
                                  }
                                >
                                  Record a payment
                                </Button>
                              )}
                              {/* A credit nobody can spend is a credit that quietly stops existing:
                                the vendor deducts it from the next bill whatever this screen says. */}
                              {credit && !invoice.referencesInvoiceId && (
                                <Button
                                  size="xs"
                                  variant="light"
                                  onClick={() =>
                                    setSpending({
                                      id: invoice.id,
                                      amountMinor: -invoice.totalMinor,
                                      currency: invoice.currency,
                                      subject: billSubject(invoice),
                                    })
                                  }
                                >
                                  Put it against a bill
                                </Button>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        );
                      },
                    )}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ),
        },
        ...(isCarrier
          ? [
              {
                title: 'Crossings they billed you for',
                description: 'What it cost to bring goods over, and when.',
                children:
                  crossings.length === 0 ? (
                    <EmptyNote>No crossings in this period.</EmptyNote>
                  ) : (
                    <Stack gap={4}>
                      {crossings.map((c) => (
                        <Group key={c.id} gap="md" wrap="nowrap">
                          <Text size="sm" style={{ width: 120 }}>
                            {formatDate(c.chargedAt)}
                          </Text>
                          <Text size="sm" c="dimmed" style={{ flex: 1 }}>
                            {c.reference || 'No reference'}
                          </Text>
                          <Text size="sm" fw={600}>
                            {formatMoney(c.amountMinor, c.currency)}
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                  ),
              },
            ]
          : []),
        {
          // Under the orders and the bills, because it is a question asked once a month rather than
          // every morning — and it renders nothing at all when there is nothing to say.
          title: 'How they do',
          description: 'What you ordered from them against what actually turned up.',
          children: supplier ? <TheirRecord supplierId={supplier.id} /> : null,
        },
        {
          title: 'What you buy from them',
          description:
            'What they charge, per the unit you count in. An order to them is placed against this list.',
          children: (
            <SupplierCatalogSection
              supplierId={supplierId}
              supplierName={supplier?.name ?? 'them'}
              currency={supplier?.invoiceCurrency ?? 'HNL'}
            />
          ),
        },
      ]}
    >
      {paying && (
        <RecordPaymentForm
          bill={paying}
          onClose={() => setPaying(null)}
          onSaved={() => {
            // This page's own list, and the Bills-to-pay tab that shares the payables cache.
            void queryClient.invalidateQueries({
              queryKey: listKey(SUPPLIER_INVOICES_PATH, { supplierId }),
            });
            void queryClient.invalidateQueries({ queryKey: listKey(PAYABLES_PATH) });
          }}
        />
      )}
      {spending && supplier && (
        <ApplyCreditForm
          vendor={supplier}
          credit={spending}
          onClose={() => setSpending(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({
              queryKey: listKey(SUPPLIER_INVOICES_PATH, { supplierId }),
            });
            void queryClient.invalidateQueries({ queryKey: listKey(PAYABLES_PATH) });
          }}
        />
      )}
    </RecordScreen>
  );
}

/**
 * One vendor's record, on their own page.
 *
 * The same card the Suppliers screen ranks everyone with, so the two cannot say different things
 * about the same vendor. Half a year rather than the period selector at the top of this page: that
 * one moves what you SPENT, and a fill rate that changes when you narrow the dates is a fill rate
 * somebody will quote at a supplier and be wrong about.
 */
function TheirRecord({ supplierId }: { supplierId: string }) {
  const performance = useScorecards('180', supplierId);
  const card = performance.data?.suppliers.find((c) => c.supplierId === supplierId);

  if (performance.isLoading) return <EmptyNote>Loading…</EmptyNote>;
  if (performance.isError) {
    return <EmptyNote>This could not be loaded. Nothing has been lost — try again in a moment.</EmptyNote>;
  }
  if (!card) {
    return <EmptyNote>Nothing has arrived from them in the last six months.</EmptyNote>;
  }
  return <ScorecardCard card={card} hideName />;
}
